import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import UsageLimitComponent from "./component";

const UsageLimitContainer = (props) => {
  const { corporateId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [loading, setLoading] = useState(true);
  const [usageLimitData, setUsageLimitData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [accountType, setAccountType] = useState(null);
  const [formData, setFormData] = useState({
    usageLimitRequired: false,
    bpLimits: [],
    tagLimits: []
  });

  // Get accountType from URL query params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const type = searchParams.get('accountType');
    setAccountType(type);
  }, [location.search]);

  // Helper function to convert API frequency to UI format
  const convertApiFrequencyToUi = (frequency) => {
    if (!frequency) return "DAYS";
    const upperFreq = frequency.toUpperCase();
    if (upperFreq === "HOUR") return "HOURS";
    if (upperFreq === "DAY") return "DAYS";
    if (upperFreq === "MONTH") return "MONTHS";
    return upperFreq;
  };

  // Helper function to convert UI frequency to API format
  const convertUiFrequencyToApi = (frequency) => {
    if (!frequency) return "DAY";
    const upperFreq = frequency.toUpperCase();
    if (upperFreq === "HOURS") return "HOUR";
    if (upperFreq === "DAYS") return "DAY";
    if (upperFreq === "MONTHS") return "MONTH";
    if (upperFreq === "HOUR") return "HOUR";
    if (upperFreq === "DAY") return "DAY";
    if (upperFreq === "MONTH") return "MONTH";
    return "DAY";
  };

  // Map API to UI format based on account type
  const mapApiToUi = (apiData, accountTypeParam) => {
    // For WALLET_RFID or WALLET account type - show tagLimits
    const tagLimitsData = apiData.tagLimits || [];
    const tagLimits = tagLimitsData.map((limit) => ({
      limitOn: limit.limitOn || "AMOUNT",
      frequency: convertApiFrequencyToUi(limit.frequency),
      duration: limit.duration ? limit.duration.toString() : "1",
      value: limit.value ? limit.value.toString() : ""
    }));

    const bpLimitsData = apiData.bpLimits || [];
    const bpLimits = bpLimitsData.map((limit) => ({
      limitOn: limit.limitOn || "AMOUNT",
      frequency: convertApiFrequencyToUi(limit.frequency),
      duration: limit.duration ? limit.duration.toString() : "1",
      value: limit.value ? limit.value.toString() : ""
    }));

    const usageLimitRequired = (tagLimits.length > 0) || (bpLimits.length > 0);
    
    return { usageLimitRequired, bpLimits, tagLimits };
  };

  // Map UI to API format based on account type
  const mapUiToApi = (uiData, accountTypeParam) => {
    // Convert tagLimits to API format
    const tagLimits = uiData.tagLimits && uiData.tagLimits.length > 0
      ? uiData.tagLimits.map((limit) => ({
          limitOn: limit.limitOn || "AMOUNT",
          frequency: convertUiFrequencyToApi(limit.frequency),
          duration: parseInt(limit.duration) || 1,
          value: parseInt(limit.value) || 0
        }))
      : [];

    // Convert bpLimits to API format
    const bpLimits = uiData.bpLimits && uiData.bpLimits.length > 0
      ? uiData.bpLimits.map((limit) => ({
          limitOn: limit.limitOn || "AMOUNT",
          frequency: convertUiFrequencyToApi(limit.frequency),
          duration: parseInt(limit.duration) || 1,
          value: parseInt(limit.value) || 0
        }))
      : [];

    // Prepare accountTypes array based on accountType
    const accountTypes = accountTypeParam ? [accountTypeParam] : [];
    
    // Return payload in the required format
    return {
      usageLimitRequired: uiData.usageLimitRequired === true || uiData.usageLimitRequired === "true",
      accountTypes: accountTypes,
      tagLimits: tagLimits,
      bpLimits: bpLimits
    };
  };

  useEffect(() => {
    if (corporateId && accountType) {
      fetchUsageLimitData();
    }
  }, [corporateId, accountType]);

  const fetchUsageLimitData = () => {
    setLoading(true);
    
    // Pass accountType in the API URL
    const apiUrl = `/partner/api/v2/corporate/${corporateId}/usage-limit?account_type=${accountType}`;
    console.log("Fetching usage limits from:", apiUrl);
    
    props.getDataFromAPI(
      apiUrl,
      "GET",
      undefined,
      (response) => {
        console.log("API Response for accountType:", accountType, response);
        
        let mappedData;
        if (response && response.data) {
          mappedData = mapApiToUi(response.data, accountType);
        } else if (response && (response.tagLimits || response.bpLimits)) {
          mappedData = mapApiToUi(response, accountType);
        } else {
          mappedData = { usageLimitRequired: false, bpLimits: [], tagLimits: [] };
        }
        
        setUsageLimitData(mappedData);
        setFormData({
          usageLimitRequired: mappedData.usageLimitRequired,
          bpLimits: mappedData.bpLimits || [],
          tagLimits: mappedData.tagLimits || []
        });
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching usage limit data:", error);
        const errorData = { usageLimitRequired: false, bpLimits: [], tagLimits: [] };
        setUsageLimitData(errorData);
        setFormData(errorData);
        setLoading(false);
      }
    );
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleCancelClick = () => {
    if (usageLimitData) {
      setFormData({
        usageLimitRequired: usageLimitData.usageLimitRequired,
        bpLimits: JSON.parse(JSON.stringify(usageLimitData.bpLimits || [])),
        tagLimits: JSON.parse(JSON.stringify(usageLimitData.tagLimits || []))
      });
    }
    setIsEditMode(false);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUpdateLimit = () => {
    setLoading(true);
    
    const apiPayload = mapUiToApi(formData, accountType);
    console.log("Update Payload for accountType:", accountType, JSON.stringify(apiPayload, null, 2));
    
    const apiUrl = `/partner/api/v2/corporate/${corporateId}/usage-limit?account_type=${accountType}`;
    
    props.getDataFromAPI(
      apiUrl,
      "PUT",
      apiPayload,
      (response) => {
        console.log("Update API Response:", response);
        
        const updatedData = {
          usageLimitRequired: formData.usageLimitRequired,
          bpLimits: JSON.parse(JSON.stringify(formData.bpLimits || [])),
          tagLimits: JSON.parse(JSON.stringify(formData.tagLimits || []))
        };
        
        setUsageLimitData(updatedData);
        toastr.success("Success", "Usage limits updated successfully");
        setLoading(false);
        setIsEditMode(false);
        fetchUsageLimitData();
      },
      (error) => {
        console.error("PUT API Error:", error);
        let errorMessage = "Unable to update usage limits";
        if (error.response && error.response.data && error.response.data.status && error.response.data.status.message) {
          errorMessage = error.response.data.status.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        toastr.error("Failed", errorMessage);
        setLoading(false);
      }
    );
  };

  return (
    <UsageLimitComponent
      loading={loading}
      usageLimitData={usageLimitData}
      isEditMode={isEditMode}
      formData={formData}
      handleBackClick={handleBackClick}
      handleEditClick={handleEditClick}
      handleCancelClick={handleCancelClick}
      handleInputChange={handleInputChange}
      handleUpdateLimit={handleUpdateLimit}
      corporateId={corporateId}
      accountType={accountType}
    />
  );
};

export default connect(null, { getDataFromAPI })(UsageLimitContainer);